// ADR 30.9.26i §4 — text size as a root scale that composes with 17.9.26f's.
//
// The learner picks one of Android's five scales, stored per browser at
// `lingara.textScale`. At `s = 1` nothing is written: `index.css`'s
// `@media (min-width: 720px) { :root { font-size: 120% } }` governs exactly as
// before, and almost every learner stays on that path. At any other `s` an
// inline `font-size` on `<html>` (inline beats the `:root` rule) carries the
// product, from one pure function.
//
// Why not `calc(120% × s)` in CSS: the shell classifies in dp, reading
// `clientWidth ÷ root scale` (`useWindowClass.ts`), so a root of `1.2·s` makes
// it see `w ÷ 1.2s` — browser zoom's composition, which is the intent. The
// *edge* is what a media query cannot carry: switching the 1.2 on must never
// demote medium to compact, and with a text scale that edge is `720·s`, which
// CSS cannot take `s` into.
//
// And below the edge a scale above 1 is capped so the content never sees
// fewer than 320 CSS px (WCAG 1.4.10): a root scale grows padding as well as
// text, where Android's `fontScale` grows text only. A scale at or below 1 is
// never capped.
//
// Its own top-level folder: `ui/` and `layout/` are both at their 15-file cap.

import { MEDIUM_MIN_DP } from '../layout/windowSizeTable'

/** Android's `fontScaleOptions`, in order (`AccessibilitySettingsScreen.kt`; AC18 reads it). */
export const TEXT_SCALES = [0.85, 1, 1.15, 1.3, 1.5] as const

export type TextScale = (typeof TEXT_SCALES)[number]

/** `index.css`'s desktop root, as a number (AC17 pins the CSS literal to it). */
export const DESKTOP_ROOT_SCALE = 1.2

/** WCAG 1.4.10's reflow width: the narrowest the content may be asked to lay out at. */
export const REFLOW_MIN_PX = 320

export const TEXT_SCALE_KEY = 'lingara.textScale'

/** Dispatched on `window` after a change, so the shell re-classes without a resize. */
export const TEXT_SCALE_EVENT = 'lingara:text-scale'

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high)
}

/** The root font size in %, rounded to 0.01, or null for "leave it to index.css". */
export function rootFontPercent(w: number, s: TextScale): number | null {
  if (s === 1) return null
  let percent: number
  if (w >= MEDIUM_MIN_DP * DESKTOP_ROOT_SCALE * s) percent = 120 * s
  else if (s > 1) percent = 100 * clamp(w / REFLOW_MIN_PX, 1, s)
  else percent = 100 * s
  return Math.round(percent * 100) / 100
}

/** The stored scale; anything unreadable or unrecognised is Standard. */
export function readTextScale(): TextScale {
  try {
    const stored = Number(window.localStorage.getItem(TEXT_SCALE_KEY))
    return TEXT_SCALES.find((scale) => scale === stored) ?? 1
  } catch {
    return 1
  }
}

let current: TextScale = 1

function apply(): void {
  const root = document.documentElement
  const percent = rootFontPercent(root.clientWidth, current)
  if (percent === null) root.style.removeProperty('font-size')
  else root.style.fontSize = `${percent}%`
}

let listening = false

/**
 * Before the first render, beside `initTheme()`: synchronous, so no frame is
 * drawn at the wrong size. Installs one `resize` listener for the page's life,
 * registered before `useWindowClass`'s, so the size is re-applied before the
 * hook's one-frame-debounced measure reads it.
 */
export function initTextScale(): void {
  current = readTextScale()
  apply()
  if (listening) return
  listening = true
  window.addEventListener('resize', () => {
    if (current !== 1) apply()
  })
}

/** What the Accessibility chips call: store, apply, then tell the shell. */
export function setTextScale(scale: TextScale): void {
  current = scale
  try {
    window.localStorage.setItem(TEXT_SCALE_KEY, String(scale))
  } catch {
    // Unstorable: the size still applies to this page.
  }
  apply()
  window.dispatchEvent(new Event(TEXT_SCALE_EVENT))
}
